import type { FC, ReactNode } from 'react'
import { useCallback, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Collapse from '@mui/material/Collapse'
import SvgIcon from '@mui/material/SvgIcon'

import { RouterLink } from '@/components/RouterLink'
import { densityPx, densityPxStr } from '@/theme/base/density'
import { ChevronDown } from '@untitled-ui/icons-react'

const SUB_NAV_BORDER_WIDTH_PX = 1

interface MobileNavItemProps {
  active?: boolean
  children?: ReactNode
  depth?: number
  disabled?: boolean
  external?: boolean
  icon?: ReactNode
  label?: ReactNode
  open?: boolean
  path?: string
  title: string
}

export const MobileNavItem: FC<MobileNavItemProps> = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    icon,
    label,
    open: openProp,
    path,
    title,
  } = props
  const [open, setOpen] = useState<boolean>(!!openProp)

  const handleToggle = useCallback((): void => {
    setOpen((prevOpen) => !prevOpen)
  }, [])

  const startIcon = depth === 0 ? icon : null
  const offset = depth === 0 ? 0 : (depth - 1) * densityPx(16)

  // Branch

  if (children) {
    return (
      <li>
        <ButtonBase
          disabled={disabled}
          onClick={handleToggle}
          sx={{
            alignItems: 'center',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            pl: `${densityPx(16) + offset}px`,
            pr: `${densityPx(16)}px`,
            py: `${densityPx(6)}px`,
            textAlign: 'left',
            width: '100%',
            ...(active && {
              ...(depth === 0 && {
                backgroundColor: 'var(--nav-item-active-bg)',
              }),
            }),
            '&:hover': {
              backgroundColor: 'var(--nav-item-hover-bg)',
            },
          }}
        >
          {startIcon && (
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                justifyContent: 'center',
                mr: 2,
                ...(active && {
                  color: 'var(--nav-item-icon-active-color)',
                }),
              }}
            >
              {startIcon}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              color: 'var(--nav-item-color)',
              flexGrow: 1,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: depth > 0 ? densityPx(13) : densityPx(14),
              fontWeight: active ? 600 : 300,
              lineHeight: `${densityPx(24)}px`,
              whiteSpace: 'nowrap',
              ...(active && {
                color: 'var(--nav-item-active-color)',
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)',
              }),
            }}
          >
            {title}
          </Box>
          <Box
            aria-hidden
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--nav-item-icon-color)',
              display: 'inline-flex',
              ml: 2,
            }}
          >
            <SvgIcon
              sx={{
                fontSize: densityPx(16),
                transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <ChevronDown />
            </SvgIcon>
          </Box>
        </ButtonBase>
        <Collapse in={open} sx={{ mt: open ? 0.5 : 0 }}>
          {children}
        </Collapse>
      </li>
    )
  }

  // Leaf

  const linkProps = path
    ? external
      ? {
          component: 'a',
          href: path,
          target: '_blank',
        }
      : {
          component: RouterLink,
          href: path,
        }
    : {}

  return (
    <li>
      <ButtonBase
        disabled={disabled}
        sx={{
          alignItems: 'center',
          ...(depth > 0 && {
            borderLeft: `${SUB_NAV_BORDER_WIDTH_PX}px solid`,
            borderLeftColor: active ? 'primary.main' : 'var(--nav-item-color)',
            borderRadius: 0,
          }),
          ...(depth === 0 && {
            borderRadius: 1,
          }),
          display: 'flex',
          justifyContent: 'flex-start',
          pl: `${densityPx(16) + offset}px`,
          pr: `${densityPx(16)}px`,
          py: `${densityPx(6)}px`,
          textAlign: 'left',
          width: '100%',
          ...(active && {
            ...(depth === 0 && {
              backgroundColor: 'var(--nav-item-active-bg)',
            }),
          }),
          '&:hover': {
            backgroundColor: 'var(--nav-item-hover-bg)',
          },
        }}
        {...linkProps}
      >
        {startIcon && (
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--nav-item-icon-color)',
              display: 'inline-flex',
              justifyContent: 'center',
              mr: 2,
              ...(active && {
                color: 'var(--nav-item-icon-active-color)',
              }),
            }}
          >
            {startIcon}
          </Box>
        )}
        <Box
          component="span"
          sx={{
            color: 'var(--nav-item-color)',
            flexGrow: 1,
            fontFamily: (theme) => theme.typography.fontFamily,
            fontSize: depth > 0 ? densityPx(13) : densityPx(14),
            fontWeight: depth > 0 ? 500 : 600,
            lineHeight: `${densityPx(24)}px`,
            whiteSpace: 'nowrap',
            ...(active && {
              color: 'var(--nav-item-active-color)',
            }),
            ...(disabled && {
              color: 'var(--nav-item-disabled-color)',
            }),
          }}
        >
          {title}
        </Box>
        {label && (
          <Box component="span" sx={{ ml: 2 }}>
            {label}
          </Box>
        )}
      </ButtonBase>
    </li>
  )
}
